import { useState, useEffect, createContext, useContext, ReactNode } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signUp: (email: string, password: string) => Promise<{ error: any }>;
  signIn: (email: string, password: string) => Promise<{ error: any }>;
  signOut: () => Promise<void>;
  updateProfile: (updates: { username?: string; display_name?: string; image?: string; bio?: string }) => Promise<{ error: any }>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  useEffect(() => {
    // Set up auth state listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        setSession(session);
        setUser(session?.user ?? null);
        setLoading(false);

        // Create or update profile when user signs up or signs in
        if (session?.user && event === 'SIGNED_IN') {
          setTimeout(() => {
            ensureProfile(session.user);
          }, 0);
        }
      }
    );

    // Check for existing session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const ensureProfile = async (user: User) => {
    try {
      // Use the new atomic ensure_user_profile function (no email needed)
      const { error } = await supabase.rpc('ensure_user_profile');

      if (error) {
        console.error('Profile creation error:', error);
        toast({
          variant: "destructive",
          title: "Profile Creation Failed",
          description: `${error.code}: ${error.message}`,
        });
      }
    } catch (error: any) {
      console.error('Unexpected profile creation error:', error);
      toast({
        variant: "destructive", 
        title: "Unexpected Error",
        description: error.message || "Failed to create user profile",
      });
    }
  };

  // Helper function to get the correct base URL for email redirects
  const getBaseUrl = () => {
    // For development, use your deployed production URL
    // Replace with your actual deployed URL (e.g., https://your-app.lovableproject.com)
    return window.location.hostname === 'localhost' 
      ? 'https://your-app.lovableproject.com' // Update this with your actual deployed URL
      : window.location.origin;
  };

  const signUp = async (email: string, password: string) => {
    const redirectUrl = `${getBaseUrl()}/onboarding`;
    
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: redirectUrl,
        data: {
          full_name: email.split('@')[0],
        }
      }
    });

    if (error) {
      toast({
        variant: "destructive",
        title: "Sign up failed",
        description: error.message,
      });
    } else {
      toast({
        title: "Check your email",
        description: "We've sent you a confirmation link.",
      });
    }

    return { error };
  };

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    if (error) {
      toast({
        variant: "destructive",
        title: "Sign in failed",
        description: error.message,
      });
    }

    return { error };
  };

  const signOut = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) {
      toast({
        variant: "destructive",
        title: "Sign out failed",
        description: error.message,
      });
    }
  };

  const updateProfile = async (updates: { username?: string; display_name?: string; image?: string; bio?: string }) => {
    if (!user) return { error: 'No user logged in' };

    // Update the profiles table
    const { error } = await supabase
      .from('profiles')
      .update(updates)
      .eq('id', user.id);

    if (error) {
      console.error('Profile update error:', error);
      toast({
        variant: "destructive", 
        title: "Profile update failed",
        description: `${error.code}: ${error.message}`,
      });
    } else {
      toast({
        title: "Profile updated",
        description: "Your profile has been updated successfully.",
      });
    }

    return { error };
  };

  return (
    <AuthContext.Provider value={{
      user,
      session,
      loading,
      signUp,
      signIn,
      signOut,
      updateProfile,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}